'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const supabase = createClient();

  const fetchUserRole = async (currentUser: any) => {
    if (!currentUser) {
      setUserRole(null);
      return;
    }
    // First check metadata (fastest, no extra query)
    const metaRole =
      currentUser.user_metadata?.role ||
      currentUser.app_metadata?.role ||
      null;

    if (metaRole) {
      setUserRole(metaRole);
      return;
    }

    // Fallback: query user_profiles table
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', currentUser.id)
        .maybeSingle();
      setUserRole(data?.role ?? 'customer');
    } catch {
      setUserRole('customer');
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      fetchUserRole(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      fetchUserRole(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
          role: (metadata as any)?.role || 'customer',
          tavern_name: (metadata as any)?.tavernName || '',
        },
        // No emailRedirectTo — avoids triggering confirmation email flow
      },
    });
    if (error) throw error;

    // If email confirmation is disabled in Supabase, signUp returns a session directly.
    // If it's still enabled, session will be null — we attempt signIn to get a real session.
    if (!data.session) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      // If signIn fails (e.g. confirmation still required), surface a clear message
      if (signInError) {
        // Account was created but email confirmation is still required in Supabase settings.
        // Instruct the user to disable "Confirm Email" in Supabase Auth → Settings.
        throw new Error(
          'Account created but email confirmation is required. Please disable "Confirm Email" in your Supabase Auth settings (Authentication → Settings → Disable email confirmations) and try again.'
        );
      }
      return signInData;
    }

    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string, remember = false) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    // Extend session to 90 days when "Remember me" is checked
    if (remember && data.session) {
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      // Store a flag so the client knows to persist the 90-day preference
      if (typeof window !== 'undefined') {
        localStorage.setItem('rememberMe', '1');
        localStorage.setItem('rememberMeExpiry', String(Date.now() + 90 * 24 * 60 * 60 * 1000));
      }
    } else {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberMeExpiry');
      }
    }

    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Convenience: check if current user is super_admin
  const isSuperAdmin = userRole === 'super_admin';

  // Convenience: check if current user is a tavern owner
  const isOwner = userRole === 'tavern_owner';

  // Unified account: any authenticated user (including tavern_owner) can make
  // customer bookings. The bookings table accepts any user_profiles.id as
  // customer_id regardless of role — this flag signals that the user can
  // access the customer booking flow with their current session.
  const canBookAsCustomer = !!user && userRole !== null;

  const value = {
    user,
    session,
    loading,
    userRole,
    isSuperAdmin,
    isOwner,
    canBookAsCustomer,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
